import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Grid, Tooltip, FormControlLabel, Checkbox, Paper, Box} from "@material-ui/core";
import InfoRoundedIcon from "@material-ui/icons/InfoRounded";

// --- Local Imports ---
import * as currentPageActions from "../../../../../../../store/actions/currentPageActions";
import A_Textbox from "../../../../../Atoms/Inputs/TextFields/A_TextField";
import A_Select from "../../../../../Atoms/Inputs/Selects/A_Select";
import A_Button from "../../../../../Atoms/Inputs/Buttons/A_Button";
import A_Snackbar from "../../../../../Atoms/Feedback/Snackbars/A_Snackbar";
import { formatTypes } from "./DateFormatTypes";
import {
    fieldTypes,
    sourceTypes,
    clickToAction,
    currencyTypes,
} from "./FieldDataTypes";
import { radioFields } from "./radioFields";
import { message } from "../../../../../../Utils/common/messages/message";
import * as commonFunctions from "../../../../../../../Utils/Helpers/common";
import { isAlreadyExists, isInput } from "../../../../../../../Utils/Helpers/fieldValidation";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '83vw',
    },
    header: {
        padding: theme.spacing(2, 4),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#ffffff',
        flexShrink: 0,
    },
    formContainer: {
        flexGrow: 1,
        overflowY: 'auto',
        padding: theme.spacing(3),
    },
    columnPaper: {
        padding: theme.spacing(3),
        borderRadius: theme.shape.borderRadius * 2,
    },
    columnTitle: {
        marginBottom: theme.spacing(3),
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    fieldRow: {
        marginBottom: theme.spacing(2.5),
    },
    label: {
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.secondary,
        paddingTop: theme.spacing(1),
    },
    requiredMark: {
        color: theme.palette.error.main,
        marginLeft: theme.spacing(0.5),
    },
    fieldIcons: {
        cursor: "pointer",
        marginLeft: theme.spacing(1),
        color: theme.palette.action.active,
    },
    checkboxGroup: {
        paddingLeft: theme.spacing(1),
    },
    footer: {
        position: 'sticky',
        bottom: 0,
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#ffffff',
        display: 'flex',
        justifyContent: 'flex-end',
        zIndex: 1,
    },
    button: {
        marginLeft: theme.spacing(2),
    },
}));

const ManageFieldsForm = (props) => {
    const {
        data,
        currentPage,
        tenantId,
        destinationVertices,
        resultTemplate,
        getValidations,
        getDestinationVertices,
        getResultTemplate,
        manageFields,
        onSave,
    } = props;

    const classes = useStyles();

    const isAdd =
        props.data.id == undefined || props.data.id == 0 ? true : false;

    const [formData, setFormData] = useState(data);
    const [errors, setErrors] = useState({});
    const [changesSaved, setChangesSaved] = useState({
        status: false,
        msg: "msg",
        type: "",
    });
    const [localResultTemplates, setLocalResultTemplates] = useState([]);

    useEffect(() => {
        getValidations(currentPage);
        getDestinationVertices({ tenantId });

        if (data && data.destinationVertex) {
            const vertex = destinationVertices && destinationVertices.find(v => v.url === data.destinationVertex);
            if (vertex && vertex.id) {
                getResultTemplate({ tenantId, pageId: vertex.id });
            }
        }
    }, []);

    useEffect(() => {
        setLocalResultTemplates(resultTemplate);
    }, [resultTemplate]);

    const destinationVertexName = useMemo(() => {
        if (data && data.destinationVertex && destinationVertices && destinationVertices.length) {
            const vertex = destinationVertices.find(item => item.url === data.destinationVertex);
            return vertex ? vertex.name : data.destinationVertex;
        }
        return formData.destinationVertex || "";
    }, [formData.destinationVertex, destinationVertices, data]);

    const optionSets = useMemo(() =>
        commonFunctions.convertToLabelValueFormat(
            "id",
            "optionSetName",
            currentPage.optionSets,
            true
        ), [currentPage.optionSets]
    );

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        const newFormData = { ...formData, [id]: value };
        setFormData(newFormData);
        validateForm(newFormData);
    };

    const handleInputBlur = (e) => {
        const { id, value } = e.target;
        const newFormData = { ...formData, [id]: value.trim() };
        setFormData(newFormData);
        validateForm(newFormData);
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        const newFormData = { ...formData, [name]: checked };
        setFormData(newFormData);
        validateForm(newFormData);
    };

    const handleDropdownChange = (selectedOption, id) => {
        let newFormData = { ...formData, [id]: selectedOption.value };

        if (id === "destinationVertex") {
            newFormData.destinationVertex = selectedOption.url;
            getResultTemplate({ tenantId, pageId: selectedOption.id });
        }

        if (id === "fieldType") {
            newFormData = {
                ...newFormData,
                edgeLabel: "",
                destinationVertex: "",
                optionSet: "",
                formatType: null,
                SingleCurrencyField: "",
            };
        }
        setFormData(newFormData);
        validateForm(newFormData);
    };

    const validateForm = (dataToValidate) => {
        const newErrors = {};
        const { name, fieldType, edgeLabel, destinationVertex, resultTemplate, optionSet, align, sourceType, sourceId, SingleCurrencyField } = dataToValidate;

        if (!isInput(name)) {
            newErrors.name = "Name is required";
        } else if (isAdd && isAlreadyExists(name, "name", currentPage.fields)) {
            newErrors.name = "Name already exists.";
        }
        if (!isInput(fieldType)) newErrors.fieldType = "Field type is required";
        if (fieldType === "lookup") {
            if (!isInput(edgeLabel)) newErrors.edgeLabel = "Edge label is required";
            if (!isInput(destinationVertex)) newErrors.destinationVertex = "Destination vertex is required";
            if (!isInput(resultTemplate)) newErrors.resultTemplate = "Result template is required";
        } else if (fieldType === "optionset" && sourceType === "" && !isInput(optionSet)) {
            newErrors.optionSet = "Optionset is required";
        } else if (fieldType === "radios") {
            if (!isInput(optionSet)) newErrors.optionSet = "Optionset is required";
            if (!isInput(align)) newErrors.align = "Alignment is required";
        } else if (fieldType === "multiselect" && !isInput(optionSet)) {
            newErrors.optionSet = "Optionset is required";
        }
        if (sourceType && !isInput(sourceId)) {
            newErrors.sourceId = "Source id is required";
        }
        if (fieldType === "SingleCurrencyField" && !isInput(SingleCurrencyField)) {
            newErrors.SingleCurrencyField = "Currency Type is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (validateForm(formData)) {
            let payload = { ...formData };
            if (payload.fieldType !== "date") {
                payload.formatType = null;
            }

            let fields = [...currentPage.fields];
            if (isAdd) {
                payload.id = fields.length + 1;
                payload.isInverse = (payload.isInverse !== null && payload.isInverse !== undefined) ? payload.isInverse : false;
                fields.push(payload);
            } else {
                fields = fields.map(field => field.id === formData.id ? payload : field);
            }

            manageFields(fields);
            setChangesSaved({
                status: true,
                msg: "Changes saved successfully!",
                type: "success",
            });
            setTimeout(() => {
                onSave();
            }, 1000);
        } else {
            setChangesSaved({
                status: true,
                msg: "Please fill in the required details.",
                type: "error",
            });
        }
    };

    const isJSONValid = (str) => {
        try {
            JSON.parse(str);
            return true;
        } catch (e) {
            return false;
        }
    };

    const InfoTag = ({ msg }) => (
        <Tooltip title={msg}>
            <InfoRoundedIcon className={classes.fieldIcons} />
        </Tooltip>
    );

    const renderField = (label, id, component, required = false, infoMsg = null) => (
        <Grid container alignItems="flex-start" className={classes.fieldRow}>
            <Grid item xs={4}>
                <Typography className={classes.label}>
                    {label}
                    {required && <span className={classes.requiredMark}>*</span>}
                    {infoMsg && <InfoTag msg={infoMsg} />}
                </Typography>
            </Grid>
            <Grid item xs={8}>
                {component}
            </Grid>
        </Grid>
    );

    const nonValidationsFields = [
        "SingleCurrencyField",
        "multiselect",
        "section",
        "button",
        "radios",
        "html",
        "Document",
        "PortfolioFileUpload",
        "lookup",
        "bit",
        "optionset"
    ]

    return (
        <Box className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h5" color="textPrimary" style={{ fontWeight: 600 }}>
                    {isAdd ? "Create New Field" : `Editing: ${data.name}`}
                </Typography>
            </div>

            <div className={classes.formContainer}>
                <Grid container spacing={2}>
                    {/* --- LEFT COLUMN --- */}
                    <Grid item xs={12} md={6}>
                        <Paper className={classes.columnPaper} elevation={2}>
                            {renderField("Name", "name", <A_Textbox id="name" fullWidth defaultValue={formData.name} error={!!errors.name} helperText={errors.name} onChange={handleInputChange} onBlur={handleInputBlur} />, true)}
                            <Grid container className={classes.checkboxGroup}>
                                <Grid item xs={4}></Grid>
                                <Grid item xs={8}>
                                    <FormControlLabel control={<Checkbox name="isPageTitle" color="primary" checked={!!formData.isPageTitle} onChange={handleCheckboxChange} />} label="Is Title" />
                                    <FormControlLabel control={<Checkbox name="isSearchable" color="primary" checked={!!formData.isSearchable} onChange={handleCheckboxChange} />} label="Is Searchable" />
                                    <FormControlLabel control={<Checkbox name="inNewTab" color="primary" checked={!!formData.inNewTab} onChange={handleCheckboxChange} />} label="Open in New Tab" />
                                    <FormControlLabel control={<Checkbox name="inLink" color="primary" checked={!!formData.inLink} onChange={handleCheckboxChange} />} label="Link Open" />
                                </Grid>
                            </Grid>
                            {renderField("Type", "fieldType", <A_Select id="fieldType" variant={"standard"} fullWidth defaultValue={formData.fieldType || ""} error={!!errors.fieldType} helperText={errors.fieldType} options={fieldTypes} onChange={handleDropdownChange} disabled={!formData.name} />, true)}
                            {renderField("Description", "description", <A_Textbox id="description" fullWidth multiline rows={4} defaultValue={formData.description} onChange={handleInputChange} onBlur={handleInputBlur} />)}
                            {renderField("Helper Text", "fieldHelperText", <A_Textbox id="fieldHelperText" fullWidth multiline rows={3} defaultValue={formData.fieldHelperText} onChange={handleInputChange} onBlur={handleInputBlur} />)}
                        </Paper>
                    </Grid>

                    {/* --- RIGHT COLUMN --- */}
                    <Grid item xs={12} md={6}>
                        <Paper className={classes.columnPaper} elevation={2}>
                            <Typography variant="h6" className={classes.columnTitle}>
                                Other Configuration
                            </Typography>

                            {formData.fieldType === 'SingleCurrencyField' && renderField("Currency Type", "SingleCurrencyField", <A_Select id="SingleCurrencyField" variant={"standard"} fullWidth defaultValue={formData.SingleCurrencyField || ""} error={!!errors.SingleCurrencyField} helperText={errors.SingleCurrencyField} options={currencyTypes} onChange={handleDropdownChange} />, true)}

                            {/* --- Optionset / Multiselect / Radios Source --- */}
                            {["optionset", "radios", "multiselect"].includes(formData.fieldType) && !formData.sourceType && renderField("Optionset", "optionSet", <A_Select id="optionSet" variant={"standard"} fullWidth defaultValue={formData.optionSet || ""} helperText={errors.optionSet} error={!!errors.optionSet} options={optionSets} onChange={handleDropdownChange} />, true)}


                            {/* --- Optionset/Source Fields --- */}
                            {(formData.fieldType === "optionset" && !formData.optionSet) && renderField("Source Type", "sourceType", <A_Select id="sourceType" variant={"standard"} fullWidth defaultValue={formData.sourceType} options={sourceTypes} onChange={handleDropdownChange} />, false, message.fieldSourceType)}
                            {!!formData.sourceType && renderField("Source ID", "sourceId", <A_Textbox id="sourceId" fullWidth defaultValue={formData.sourceId} error={!!errors.sourceId} helperText={errors.sourceId} onChange={handleInputChange} onBlur={handleInputBlur} />, true, message.fieldSourceId)}
                            {!!formData.sourceType && renderField("Parameters", "parameter", <A_Textbox id="parameter" fullWidth multiline rows={3} defaultValue={formData.parameter || "{}"} onChange={handleInputChange} onBlur={handleInputBlur} error={formData.parameter && !isJSONValid(formData.parameter)} helperText={formData.parameter && !isJSONValid(formData.parameter) ? "Please enter valid JSON." : ""} />, false, message.fieldParameter)}
                            {!!formData.sourceType && <FormControlLabel
                                control={
                                    <Checkbox
                                        name="multiSelectWithSearch"
                                        color="primary"
                                        checked={
                                            !!formData.multiSelectWithSearch
                                        }
                                        onChange={handleCheckboxChange}
                                    />
                                }
                                label="Enable Searchable Multi-select"
                                color="primary"
                            />}

                            {/* --- Date Formatting --- */}
                            {formData.fieldType === "date" && renderField("Format", "formatType",
                                <>
                                    <A_Select id="formatType" variant={"standard"} fullWidth defaultValue={formData.formatType || ""} onChange={handleDropdownChange} options={formatTypes} />
                                    <FormControlLabel control={<Checkbox name="lettersAllowed" color="primary" checked={!!formData.lettersAllowed} onChange={handleCheckboxChange} />} label="Letters Allowed" />
                                    <FormControlLabel control={<Checkbox name="defaultDateValue" color="primary" checked={!!formData.defaultDateValue} onChange={handleCheckboxChange} />} label="Default to Today" />
                                    <FormControlLabel control={<Checkbox name="showInLocalTimeZone" color="primary" checked={!!formData.showInLocalTimeZone} onChange={handleCheckboxChange} />} label="Use Local Timezone" />
                                </>
                            )}
                            {/* --- Radio Button Alignment --- */}
                            {formData.fieldType === "radios" && renderField("Align", "align", <A_Select id="align" variant={"standard"} fullWidth defaultValue={formData.align || ""} error={!!errors.align} helperText={errors.align} options={radioFields || []} onChange={handleDropdownChange} />, true)}

                            {/* --- Lookup Fields --- */}
                            {formData.fieldType === "lookup" && renderField("Edge Label", "edgeLabel", <A_Textbox id="edgeLabel" fullWidth defaultValue={formData.edgeLabel} helperText={errors.edgeLabel} error={!!errors.edgeLabel} onChange={handleInputChange} onBlur={handleInputBlur} />, true)}
                            {formData.fieldType === "lookup" && renderField("Destination Vertex", "destinationVertex", <A_Select id="destinationVertex" variant={"standard"} fullWidth defaultValue={destinationVertexName || ""} helperText={errors.destinationVertex} error={!!errors.destinationVertex} options={destinationVertices || []} onChange={handleDropdownChange} />, true)}
                            {formData.fieldType === "lookup" && renderField("Result Template", "resultTemplate", <A_Select id="resultTemplate" variant={"standard"} fullWidth defaultValue={formData.resultTemplate || ""} helperText={errors.resultTemplate} error={!!errors.resultTemplate} options={localResultTemplates || []} onChange={handleDropdownChange} />, true)}
                            {formData.fieldType === "lookup" && renderField("Is Inverse", "isInverse", <A_Select id="isInverse" variant={"standard"} fullWidth defaultValue={formData.isInverse != null ? formData.isInverse : false} options={[{ label: "Yes", value: true }, { label: "No", value: false }]} onChange={handleDropdownChange} />)}
                            {formData.fieldType === "lookup" && renderField("SP Name", "spName", <A_Textbox id="spName" fullWidth defaultValue={formData.spName} onChange={handleInputChange} onBlur={handleInputBlur} />)}

                            {/* --- Validations --- */}
                            {!nonValidationsFields.includes(formData.fieldType) && (
                                <>
                                    {renderField("Validations", "validations", <A_Select id="validations" variant={"standard"} fullWidth defaultValue={formData.validations || ""} options={props.fieldValidations || []} onChange={handleDropdownChange} />)}
                                    {renderField("Click To Action", "clickToAction", <A_Select id="clickToAction" variant={"standard"} fullWidth defaultValue={formData.clickToAction || ""} options={clickToAction} onChange={handleDropdownChange} />)}
                                </>
                            )}
                        </Paper>

                    </Grid>
                </Grid>
            </div>

            <div className={classes.footer}>
                <A_Button variant="outlined" color="default" label="Cancel" onClick={onSave} />
                <A_Button variant="contained" color="primary" label={isAdd ? "Add Field" : "Update Field"} onClick={handleSave} className={classes.button} />
            </div>

            <A_Snackbar open={changesSaved.status} message={changesSaved.msg} autoHideDuration={4000} type={changesSaved.type} horizontal="center" handleClose={() => setChangesSaved(false)} />
        </Box>
    );
};

const mapStateToProps = (state) => ({
    fieldValidations: state.fieldValidations.data,
    destinationVertices: state.destinationVertices.data,
    resultTemplate: state.resultTemplate.data,
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(currentPageActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ManageFieldsForm);